import React, { useEffect, useState } from "react";
import styles from "./WorldClassEducator.module.css";
import { RotateCcw } from "lucide-react";

interface FilterField {
  program: string;
  school: string;
}

interface Props {
  filterFieldOne: FilterField[];
  filterFieldTwo: string[];
  onClickFilter: (selectedItem: any) => void;
  data?: any;
}

const WorldClassEducator: React.FC<Props> = ({
  filterFieldOne = [],
  filterFieldTwo = [],
  onClickFilter,
  data,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [filteredPrograms, setFilteredPrograms] = useState<string[]>([]);

  const handleResetFilter = () => {
    setSelectedProgram("");
    setSelectedSchool("");
    const allCategories = filterFieldOne.map((item) => item.program);
    const uniqueCategories = Array.from(new Set(allCategories));
    setFilteredPrograms(uniqueCategories);
    onClickFilter({ school: "", program: "", resetPage: true });
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 767);
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  // Initialize filtered programs on component mount
  useEffect(() => {
    const allPrograms = filterFieldOne.map((item) => item.program);
    // Remove duplicates
    const uniquePrograms = Array.from(new Set(allPrograms));
    setFilteredPrograms(uniquePrograms);
  }, [filterFieldOne]);

  // Update filtered programs when school selection changes
  useEffect(() => {
    if (selectedSchool) {
      const programsForSchool = filterFieldOne
        .filter((item) => item.school === selectedSchool)
        .map((item) => item.program);
      setFilteredPrograms(programsForSchool);
    } else {
      const allPrograms = filterFieldOne.map((item) => item.program);
      // Remove duplicates
      const uniquePrograms = Array.from(new Set(allPrograms));
      setFilteredPrograms(uniquePrograms);
    }
  }, [selectedSchool, filterFieldOne]);

  const handleFilterSubmit = () => {
    const dataToSend = {
      school: selectedSchool,
      program: selectedProgram,
      resetPage: true,
    };
    onClickFilter(dataToSend);
  };
  const formatText = (text: string): string => {
    return text
      .replace(/\/|-/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <>
      <div className="container">
        <div className={`${styles.worldClassContainer}`}>
          <h2>{data?.title}</h2>
          <p className={styles.worldClassEduDesc}>{data?.description}</p>
        </div>
      </div>
      <div className={isMobile ? "" : "container"}>
        <div className={styles.filterContainer}>
          <div className={styles.filterItem}>
            <label className={styles.label}>Filter by Schools</label>
            <select
              className={styles.select}
              value={selectedSchool}
              onChange={(e) => {
                setSelectedSchool(e.target.value);
                setSelectedProgram("");
              }}
            >
              <option value="" disabled>
                Select School
              </option>
              {filterFieldTwo?.map((school, index) => (
                <option key={index} value={school}>
                  {formatText(school)}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterItem}>
            <label className={styles.label}>Program Affiliations</label>
            <select
              className={styles.select}
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              disabled={filteredPrograms.length === 0}
            >
              <option value="" disabled hidden>
                Select Programme
              </option>
              {filteredPrograms?.map((program, index) => (
                <option key={index} value={program}>
                  {formatText(program)}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterButtonMobile}>
            <button
              className={styles.filterButton}
              onClick={handleFilterSubmit}
              disabled={!selectedSchool && !selectedProgram}
            >
              Filter
            </button>
            {(selectedProgram || selectedSchool) && (
              <RotateCcw
                className={styles.resetButton}
                onClick={handleResetFilter}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WorldClassEducator;
